import {
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithEmailAndPassword as firebaseSignInWithEmailAndPassword,
  signInWithEmailLink,
  isSignInWithEmailLink,
  signOut,
  deleteUser,
  signInWithCustomToken,
  signInWithCredential,
  OAuthProvider,
  User,
  getAdditionalUserInfo
} from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
// @ts-ignore
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { httpsCallable } from 'firebase/functions';
import { auth, db, facebookProvider, functions } from '../../config/firebase';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';

// Basic Google provider (popup by default)
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('email');
googleProvider.addScope('profile');

// Helper to create or merge user profile in Firestore
async function createOrMergeUserProfile(user: User) {
  const userRef = doc(db, 'users', user.uid);
  const snap = await getDoc(userRef);
  const existingData = snap.exists() ? snap.data() : null;
  const payload: Record<string, any> = {
    email: user.email || '',
    displayName: user.displayName || '',
    photoURL: user.photoURL || '',
    phoneNumber: user.phoneNumber || '',
    updatedAt: new Date().toISOString()
  };
  if (!existingData || !existingData.subscription) {
    payload.subscription = 'free';
  }
  await setDoc(userRef, payload, { merge: true });
  return (await getDoc(userRef)).data();
}

export const authService = {
  async loginWithGoogle() {
    let user: User;
    if (Capacitor.isNativePlatform()) {
      const result = await FirebaseAuthentication.signInWithGoogle();
      const credential = GoogleAuthProvider.credential(result.credential?.idToken, result.credential?.accessToken);
      const userCredential = await signInWithCredential(auth, credential);
      user = userCredential.user;
    } else {
      const result = await signInWithPopup(auth, googleProvider);
      user = result.user;
    }
    await createOrMergeUserProfile(user);
    return user;
  },

  async loginWithGoogleRedirect() {
    await signInWithRedirect(auth, googleProvider);
  },

  async loginWithFacebook() {
    let user: User;
    if (Capacitor.isNativePlatform()) {
      const result = await FirebaseAuthentication.signInWithFacebook();
      const credential = FacebookAuthProvider.credential(result.credential?.accessToken as string);
      const userCredential = await signInWithCredential(auth, credential);
      user = userCredential.user;
    } else {
      const result = await signInWithPopup(auth, facebookProvider as FacebookAuthProvider);
      user = result.user;
    }
    await createOrMergeUserProfile(user);
    return user;
  },

  async loginWithApple() {
    if (Capacitor.isNativePlatform()) {
      const result = await FirebaseAuthentication.signInWithApple();
      const credential = new OAuthProvider('apple.com').credential({
        idToken: result.credential?.idToken,
        rawNonce: result.credential?.nonce
      });
      const userCredential = await signInWithCredential(auth, credential);
      await createOrMergeUserProfile(userCredential.user);
      return userCredential.user;
    } else {
      throw new Error('Apple Sign-In not configured for web');
    }
  },

  async handleRedirectResult(): Promise<{ user: User; isNewUser?: boolean } | null> {
    // Support redirect flow if used somewhere
    const res = await getRedirectResult(auth);
    if (res && res.user) {
      const isNewUser = getAdditionalUserInfo(res)?.isNewUser;
      await createOrMergeUserProfile(res.user);
      return { user: res.user, isNewUser };
    }
    return null;
  },

  async loginWithEmailAndPassword(email: string, password: string) {
    const result = await firebaseSignInWithEmailAndPassword(auth, email, password);
    await createOrMergeUserProfile(result.user);
    return result.user;
  },

  async sendEmailVerification({ email, redirectUrl: _redirectUrl }: { email: string; redirectUrl: string }) {
    // Call the Cloud Function to generate and send the PIN
    const sendPin = httpsCallable<{ email: string }, { success: boolean }>(functions, 'sendLoginPin');
    await sendPin({ email });
    
    // We still return a dummy verificationId to keep the frontend flow working
    return Math.random().toString(36).slice(2);
  },

  async loginWithEmailLink(email: string, link: string) {
    if (!isSignInWithEmailLink(auth, link)) {
      throw new Error('Invalid email link');
    }
    const cred = await signInWithEmailLink(auth, email, link);
    await createOrMergeUserProfile(cred.user);
    return cred.user;
  },

  async verifyEmailCode({ email, code }: { email: string; code: string }) {
    // Call the Cloud Function to verify the PIN
    const verifyPin = httpsCallable<{ email: string; code: string }, { token: string }>(functions, 'verifyLoginPin');
    const result = await verifyPin({ email, code });
    
    // Sign in with the returned custom token
    const cred = await signInWithCustomToken(auth, result.data.token);
    await createOrMergeUserProfile(cred.user);
    
    return true;
  },

  async sendSmsVerification({ phoneNumber: _phoneNumber }: { phoneNumber: string }) {
    // Placeholder: integrate with Firebase Phone Auth or Recaptcha flow later
    throw new Error('La vérification SMS n’est pas disponible. Utilisez la connexion par email ou Google.');
  },

  async verifySmsCode({ phoneNumber: _phoneNumber, code }: { phoneNumber: string; code: string }) {
    // Placeholder validation for UI flow
    return false;
  },

  async logout() {
    await signOut(auth);
  },

  async deleteAccount(_password?: string) {
    // Delete Firestore profile then Firebase user
    const current = auth.currentUser;
    if (!current) throw new Error('No authenticated user');

    try {
      // Delete user profile from Firestore
      await deleteDoc(doc(db, 'users', current.uid));
    } catch (error) {
      console.warn('Failed to delete user profile from Firestore:', error);
      // Continue with user deletion even if profile deletion fails
    }

    try {
      // Delete Firebase user
      await deleteUser(current);
    } catch (error) {
      console.error('Failed to delete Firebase user:', error);
      throw new Error('Failed to delete user account. Please try again or contact support.');
    }
  },

  async getUserProfile(userId: string) {
    try {
      // Les utilisateurs anonymes n'ont pas de document profil
      if (auth.currentUser?.isAnonymous) {
        return null;
      }

      const snap = await getDoc(doc(db, 'users', userId));
      return snap.exists() ? (snap.data() as any) : null;
    } catch (error) {
      console.error('Failed to get user profile:', error);
      throw new Error('Failed to retrieve user profile. Please try again.');
    }
  },

  // Backwards compatibility with existing calls
  async _createUserProfile(user: User) {
    return createOrMergeUserProfile(user);
  }
};

export default authService;


